/**
 * EulaModal — User-Agreement presentation + acceptance flow (DEV-906).
 *
 * Renders the markdown EULA from `src/content/legal/eula.en.md` (loaded
 * via Vite's `?raw` import — no async fetch needed, the markdown is
 * bundled at build time). Shows the version label, the agreement body,
 * and an Accept button that POSTs to `/api/auth/accept-eula` with the
 * frontend's EULA_CURRENT_VERSION.
 *
 * Recurring acceptance: when the EULA is updated, the constants are
 * bumped on both backend and frontend, the milestone evaluator detects
 * the version gap, and this modal re-opens for the user to accept the
 * new version. The same component handles first-time and re-acceptance
 * — there's no separate UI flow.
 *
 * Accessibility:
 *   - role="dialog" + aria-labelledby
 *   - Escape closes (when allowAbort)
 *   - Focus is trapped within the modal
 *   - The Accept button is disabled until the user has scrolled the
 *     agreement body to within 80% of the bottom — a soft proof-of-read
 *     gate that avoids forcing a full scroll for re-acceptance of
 *     minor version bumps.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { acceptEula } from '../../api/eula';
import {
  EULA_CURRENT_VERSION,
  EULA_CURRENT_VERSION_LABEL,
} from '../../content/legal/eulaConstants';
// Vite raw import — markdown is bundled at build time, no fetch required.
 
// @ts-ignore — Vite ?raw type augmentation lives in vite-env.d.ts
import eulaMarkdown from '../../content/legal/eula.en.md?raw';
import styles from './EulaModal.module.css';
import { sanitizeRichText } from '../../utils/sanitizeHtml';

// ── Props ────────────────────────────────────────────────────

export interface EulaModalProps {
  /** Whether the modal is currently visible. */
  open: boolean;
  /**
   * Called after the user successfully accepts the agreement. Host
   * should refetch security user state so the milestone evaluator
   * sees the update on the next render.
   */
  onAccepted: () => void;
  /**
   * Optional close handler. Pass null/undefined to disable closing
   * without acceptance — useful for blocking flows where the user
   * MUST accept (e.g., right after registration).
   */
  onAbort?: () => void;
  /**
   * Read-only presentation: no Accept button, no scroll gate, no
   * acceptance POST — just the agreement text and a Close button.
   * Used on PUBLIC pages (invite landing, signup) where the reader
   * is not authenticated yet: the acceptance act happens via the
   * form's checkbox, and navigating to an in-app route would boot
   * the app shell on whatever stale session the browser holds
   * (the /eula dead-link session-leak bug, 2026-07-08).
   */
  viewOnly?: boolean;
}

// ── Helpers ─────────────────────────────────────────────────

/**
 * Lightweight markdown→HTML for the EULA body. We deliberately don't
 * pull in a full markdown parser library — the EULA's markdown is
 * controlled (we author it), so the supported subset is small:
 *   - # / ## / ### headings
 *   - paragraphs
 *   - **bold** and *italic*
 *   - - bullet lists
 *   - --- horizontal rules
 *   - `code` (inline)
 * Anything more exotic (tables, images, embedded HTML) won't render
 * but also won't crash. If we need more, swap in `marked` or `markdown-it`.
 */
export function renderMarkdown(md: string): string {
  // Escape HTML first.
  let s = md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Headings — order matters (h3 before h2 before h1).
  s = s.replace(/^### (.*)$/gm, '<h3>$1</h3>');
  s = s.replace(/^## (.*)$/gm, '<h2>$1</h2>');
  s = s.replace(/^# (.*)$/gm, '<h1>$1</h1>');

  // Horizontal rule.
  s = s.replace(/^---$/gm, '<hr/>');

  // Inline emphasis.
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>');
  s = s.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Bullets — group consecutive `- ` lines into a <ul>.
  s = s.replace(/(^|\n)((?:- .*(?:\n|$))+)/g, (_match, lead, block: string) => {
    const items = block
      .trim()
      .split('\n')
      .map((line) => line.replace(/^- /, '').trim())
      .map((item) => `<li>${item}</li>`)
      .join('');
    return `${lead}<ul>${items}</ul>`;
  });

  // Checkbox bullets — Used in the acceptance summary; render as
  // styled bullets without the actual checkbox interactivity (the
  // user accepts via the modal's Accept button).
  s = s.replace(/<li>\[ \] (.*?)<\/li>/g, '<li class="eula-checklist">$1</li>');

  // Paragraphs — wrap consecutive non-tag lines.
  s = s
    .split(/\n{2,}/)
    .map((para) => {
      const trimmed = para.trim();
      if (!trimmed) return '';
      // Already a block-level tag? Leave alone.
      if (/^<(h\d|ul|ol|hr|pre|blockquote|p)/i.test(trimmed)) return trimmed;
      return `<p>${trimmed.replace(/\n/g, '<br/>')}</p>`;
    })
    .join('\n');

  return s;
}

// ── Component ────────────────────────────────────────────────

const EulaModal: React.FC<EulaModalProps> = ({ open, onAccepted, onAbort, viewOnly }) => {
  const { t } = useAppTranslation('pages');
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [scrolledEnough, setScrolledEnough] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoize the rendered HTML so we don't re-parse on every render.
  const bodyHtml = React.useMemo(() => renderMarkdown(eulaMarkdown), []);

  // Track scroll progress; enable the Accept button at 80%.
  const handleScroll = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const distance = el.scrollTop + el.clientHeight;
    const total = el.scrollHeight;
    if (total <= el.clientHeight) {
      // Body is shorter than the viewport — no scroll required.
      setScrolledEnough(true);
      return;
    }
    if (distance / total >= 0.8) {
      setScrolledEnough(true);
    }
  }, []);

  // Reset state when the modal opens fresh.
  // Note: returning `null` from this component keeps the React instance
  // mounted (state persists). Without resetting `submitting` here, a
  // successful accept on first open leaves `submitting=true` and the
  // next open shows the button stuck on "Submitting…" forever.
  useEffect(() => {
    if (open) {
      setScrolledEnough(false);
      setError(null);
      setSubmitting(false);
      // Run the scroll check once after mount in case the EULA fits
      // entirely on screen.
      requestAnimationFrame(handleScroll);
    }
  }, [open, handleScroll]);

  // Escape closes (only when abort is allowed).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onAbort) {
        onAbort();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onAbort]);

  const handleAccept = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      await acceptEula(EULA_CURRENT_VERSION);
      // Defensive — reset before notifying parent. The open-effect
      // also resets on next open, but if the parent decides NOT to
      // close the modal (e.g., shows a confirmation), we don't want
      // a phantom Submitting state lingering.
      setSubmitting(false);
      onAccepted();
    } catch (err: any) {
      setError(err?.message || t('eulaModal.errorGeneric'));
      setSubmitting(false);
    }
  }, [onAccepted, t]);

  /*
    Print handler — uses the browser's native print dialog, which doubles
    as "Save as PDF" on every modern OS. We don't ship a separate PDF
    pipeline because that would require either a server-side renderer
    (more infra) or a client-side library (more bundle weight). The
    @media print CSS strips the modal chrome and prints just the
    document body with a print-only header.
  */
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  if (!open) return null;

  return (
    <div className={styles.backdrop} role="presentation">
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="eula-modal-title"
      >
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h1 id="eula-modal-title" className={styles.title}>
              {t('eulaModal.title')}
            </h1>
            <span className={styles.versionLabel}>
              {EULA_CURRENT_VERSION_LABEL}
            </span>
          </div>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.printBtn}
              onClick={handlePrint}
              aria-label={t('eulaModal.print')}
              title={t('eulaModal.print')}
            >
              {/* Printer icon — inline SVG to avoid external dependency. */}
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
            </button>
            {onAbort && (
              <button
                type="button"
                className={styles.closeBtn}
                onClick={onAbort}
                aria-label={t('eulaModal.close')}
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/*
          Print-only document header — invisible on screen, surfaced
          when printing so the printout has a clear title and version
          label that the screen modal already shows in its chrome.
        */}
        <div className={styles.printHeader} aria-hidden="true">
          <div className={styles.printTitle}>{t('eulaModal.title')}</div>
          <div className={styles.printVersionLabel}>
            {EULA_CURRENT_VERSION_LABEL}
          </div>
        </div>

        {/* Body — scrollable agreement text */}
        <div
          ref={bodyRef}
          className={styles.body}
          onScroll={handleScroll}
          // Source is bundled markdown we authored, not user content, but we
          // still sanitize at the render boundary for defense-in-depth (one
          // consistent policy for every dangerouslySetInnerHTML sink).
          dangerouslySetInnerHTML={{ __html: sanitizeRichText(bodyHtml) }}
        />

        {/* Footer — viewOnly: just Close; otherwise Accept flow */}
        {viewOnly ? (
          <div className={styles.footer}>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={onAbort}
              >
                {t('eulaModal.close')}
              </button>
            </div>
          </div>
        ) : (
        <div className={styles.footer}>
          {error && (
            <div role="alert" className={styles.errorMessage}>
              {error}
            </div>
          )}
          {!scrolledEnough && (
            <div className={styles.scrollHint}>
              {t('eulaModal.scrollHint')}
            </div>
          )}
          <div className={styles.actions}>
            {onAbort && (
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={onAbort}
                disabled={submitting}
              >
                {t('eulaModal.cancel')}
              </button>
            )}
            <button
              type="button"
              className={styles.acceptBtn}
              onClick={handleAccept}
              disabled={!scrolledEnough || submitting}
            >
              {submitting
                ? t('eulaModal.submitting')
                : t('eulaModal.accept')}
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default EulaModal;
